import { Box, Typography, keyframes } from "@mui/material";
import type { JSX } from "react";

/** The three bouncing dots of the in-thread "typing…" bubble. */
const typingBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30% { transform: translateY(-4px); opacity: 1; }
`;

/**
 * The live typing-dots bubble at the foot of the thread (auto-expires via the
 * provider's TTL). In the room it's prefixed with who's typing; a DM just
 * shows the dots. Extracted from `ChatThread.tsx`.
 */
export function TypingIndicator({
  showSenderNames,
  typingName,
}: {
  showSenderNames: boolean;
  typingName: string | null;
}): JSX.Element {
  return (
    <Box
      data-testid="chat-typing-indicator"
      sx={{ display: "flex", justifyContent: "flex-start", mt: 0.75 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          pl: 1.25,
          pr: 1.5,
          py: 0.85,
          bgcolor: "chat.bubbleIn",
          color: "chat.bubbleInText",
          boxShadow: 1,
          borderRadius: 1.5,
          borderTopLeftRadius: 2,
        }}
      >
        {showSenderNames && typingName && (
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "chat.accent", mb: 0.25 }}>
            {typingName}
          </Typography>
        )}
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: "chat.meta",
                animation: `${typingBounce} 1.2s infinite`,
                animationDelay: `${i * 0.18}s`,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
